import type { SpendingControls } from "@/types_api/backend/transport/overage_acceptance_offer_response"
import type { OverageAcceptanceRequest } from "@/types_api/backend/transport/overage_acceptance_request"
import type { OverageAcceptanceReceipt } from "@/types_api/backend/transport/overage_acceptance_response"

import type { PaidOverageReviewPort } from "../hooks/usePaidOverageReview"
import {
  paidOverageCanonicalJson,
  paidOverageTimestamp,
  verifyPaidOverageControls,
  type readPaidOverageReviewKey,
} from "./paidOverageReview"

type Snapshot = ReturnType<typeof readPaidOverageReviewKey>

function originalRequest(request: OverageAcceptanceRequest) {
  return {
    ...request,
    recovery_request_id: request.recovery_request_id ?? null,
  }
}

export function verifyPaidOverageReceipt(
  snapshot: Snapshot,
  request: OverageAcceptanceRequest,
  receipt: OverageAcceptanceReceipt,
): Awaited<ReturnType<PaidOverageReviewPort["decide"]>> {
  const offer = snapshot.response.offer
  if (offer === null) {
    throw new Error("The complete original offer is required.")
  }
  const expectedOutcome =
    request.decision === "decline"
      ? "declined"
      : offer.payment_state === "ready"
        ? "accepted"
        : offer.payment_state === "setup_required"
          ? "payment_setup_required"
          : "payment_authentication_required"
  const completed =
    expectedOutcome === "accepted" || expectedOutcome === "declined"
  const accepted = expectedOutcome === "accepted"
  const expectedControls = completed
    ? `${accepted ? "member-acceptance" : "member-decline"}:${request.request_id}`
    : null
  const recorded = paidOverageTimestamp(receipt.recorded_at)
  if (
    receipt.organization_id !== snapshot.response.organization_id ||
    receipt.actor_id !== snapshot.actorId ||
    receipt.reference !== `overage-acceptance:${request.request_id}` ||
    paidOverageCanonicalJson(originalRequest(receipt.request)) !==
      paidOverageCanonicalJson(originalRequest(request)) ||
    paidOverageCanonicalJson(receipt.offer) !==
      paidOverageCanonicalJson(offer) ||
    receipt.outcome !== expectedOutcome ||
    receipt.controls_revision_id !== expectedControls ||
    recorded < paidOverageTimestamp(offer.effective_start) ||
    recorded >= paidOverageTimestamp(offer.effective_end) ||
    (accepted
      ? receipt.effective_at === null ||
        receipt.effective_at !== receipt.recorded_at
      : receipt.effective_at !== null)
  ) {
    throw new Error("The original decision receipt could not be verified.")
  }
  return {
    scopeKey: snapshot.scopeKey,
    attemptId: request.request_id,
    reference: receipt.reference,
    result:
      expectedOutcome === "accepted" || expectedOutcome === "declined"
        ? expectedOutcome
        : expectedOutcome === "payment_setup_required"
          ? "payment-setup"
          : "payment-authentication",
  }
}

export function verifyPaidOverageOriginalControls(
  snapshot: Snapshot,
  request: OverageAcceptanceRequest,
  receipt: OverageAcceptanceReceipt,
  originalControls: SpendingControls | null,
) {
  verifyPaidOverageReceipt(snapshot, request, receipt)
  if (receipt.controls_revision_id === null) {
    if (originalControls !== null) {
      throw new Error(
        "Pending payment cannot establish original paid controls.",
      )
    }
    return
  }
  if (originalControls === null) {
    throw new Error("The original immutable controls are required.")
  }
  verifyPaidOverageControls(originalControls, snapshot.response.organization_id)
  if (
    originalControls.revision_id !== receipt.controls_revision_id ||
    originalControls.currency !== receipt.offer.terms.currency ||
    originalControls.overages_enabled !== (receipt.outcome === "accepted") ||
    (receipt.outcome === "accepted" &&
      (originalControls.limit_state !== request.limit_state ||
        originalControls.limit_minor !== request.limit_minor)) ||
    (receipt.outcome === "declined" &&
      (originalControls.limit_state !==
        snapshot.response.controls?.limit_state ||
        originalControls.limit_minor !==
          snapshot.response.controls.limit_minor))
  ) {
    throw new Error(
      "The original controls do not match the immutable decision.",
    )
  }
}
